import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  adminUserDetailSchema,
  adminUserListSchema,
  cabinetResultSchema,
  type AdminUserDetail,
  type AdminUserList,
  type SetCabinetGoldInput,
  type CabinetResult,
} from '../schemas';
import { useToken } from './use-token';

export type AdminUserListParams = {
  page?: number;
  limit?: number;
  search?: string;
  /** Restreint aux cabinets rattachés à cette spécialité Doctolib. */
  specialiteId?: number;
  /** Filtre d'abonnement : `essai`, `expire`, `solo`, `cabinet`, `centre`, `gold`, `impaye`, `resilie`. */
  abonnement?: string;
  /** `never` : extension jamais synchronisée ; `stale` : relevé ancien. */
  sync?: string;
};

/**
 * Comptes responsables de cabinet, paginés, un client par ligne. Réservé au
 * back-office : le backend ne scope à aucun cabinet, contrairement à
 * `useCabinetUsers`.
 */
export function useAdminUsers({
  page = 1,
  limit = 20,
  search = '',
  specialiteId,
  abonnement,
  sync,
}: AdminUserListParams = {}) {
  const api = useApi();
  const token = useToken();

  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) query.set('search', search);
  if (specialiteId !== undefined) query.set('specialiteId', String(specialiteId));
  if (abonnement) query.set('abonnement', abonnement);
  if (sync) query.set('sync', sync);

  return useQuery({
    queryKey: queryKeys.adminUsers.list({ page, limit, search, specialiteId, abonnement, sync }),
    queryFn: () => api.get<AdminUserList>(`/admin/users?${query}`, { schema: adminUserListSchema }),
    enabled: token !== null,
    // Garde la page précédente affichée pendant le chargement de la suivante :
    // le tableau ne clignote pas à chaque changement de page.
    placeholderData: keepPreviousData,
  });
}

/** Fiche complète d'un compte : son cabinet, ses accès, ses abonnements. */
export function useAdminUserDetail(id: string) {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.adminUsers.detail(id),
    queryFn: () =>
      api.get<AdminUserDetail>(`/admin/users/${id}`, { schema: adminUserDetailSchema }),
    enabled: token !== null && Boolean(id),
  });
}

/**
 * Ouvre ou coupe l'accès de tout un cabinet.
 *
 * Invalide la liste **et** les fiches : la bascule se déclenche depuis le
 * tableau, mais l'état qu'elle change s'affiche aussi sur le détail de chaque
 * compte du cabinet.
 */
export function useSetCabinetActif() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cabinetId, actif }: { cabinetId: string; actif: boolean }) =>
      api.patch<CabinetResult>(
        `/admin/cabinets/${cabinetId}/actif`,
        { actif },
        { schema: cabinetResultSchema },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.all() }),
  });
}

/**
 * Accorde ou retire la formule Gold. Aucun appel Stripe : Gold est un drapeau
 * en base, il ne touche ni à la facturation ni à l'abonnement en cours.
 */
export function useSetCabinetGold() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cabinetId, gold }: SetCabinetGoldInput & { cabinetId: string }) =>
      api.patch<CabinetResult>(
        `/admin/cabinets/${cabinetId}/gold`,
        { gold },
        { schema: cabinetResultSchema },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.all() }),
  });
}

/** Prolonge l'essai gratuit d'un cabinet de `jours` jours. */
export function useExtendCabinetTrial() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cabinetId, jours }: { cabinetId: string; jours: number }) =>
      api.patch<CabinetResult>(
        `/admin/cabinets/${cabinetId}/essai`,
        { jours },
        { schema: cabinetResultSchema },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.all() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOverview.all() });
    },
  });
}
